import { Accordion, AccordionDetails, AccordionSummary, Box } from "@mui/material";
import { useSignal } from "@preact/signals-react";
import { isEmpty, isNil } from "lodash-es";
import NGIcon from "../../components/NGIcon/NGIcon";
import { isPickerInteractionActive } from "../NGDatePicker/pickerGuard";
import NGLayoutItem from "../../generators/NGLayoutItem";
import NGLayout from "../../layouts/NGLayout";
import { publishUserAction } from "../../library/analytics";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { log } from "../../library/logger";
import { isLayout } from "../../library/metadataUtils";
import { IAccordionGroupProps } from "../../library/NGFieldExtensions";
import { getClassName, getsxObject, getTestId, partitionMUIClasses } from "../../library/utils";
import { Icon } from "../../../resolvers-types";

const tag = "NGAccordionGroup";

export default function NGAccordionGroup({ config, context, layoutItem }: IAccordionGroupProps) {
  const local = setupLocalState(
    config,
    {
      Style: useSignal(config.Style ?? {}),
      AccordionContainerStyle: useSignal(config.AccordionContainerStyle ?? {}),
      Classes: useSignal(config.Classes ?? []),
      Visible: useSignal(config.Visible ?? true),
      HideExpandIcon: useSignal(config.HideExpandIcon ?? false),
      ExpandDisabled: useSignal(config.ExpandDisabled ?? false),
      Items: useSignal(config.Items ?? []),
      Expanded: useSignal(config.Expanded ?? false),
      Label: useSignal(config.Label ?? "Label"),
      LayoutItem: useSignal(layoutItem),
      Header: useSignal(config.Header ?? {}),
    },
    context
  );

  const handlers = setupHandlers(config, context);

  function collectInteractiveItems(items: any[], componentsToStopPropagation: string[]): any[] {
    if (!items) return [];

    return items.flatMap((item) => {
      const self = item?.__typename && componentsToStopPropagation.includes(item.__typename) ? [item] : [];
      const children = item?.Items ? collectInteractiveItems(item.Items, componentsToStopPropagation) : [];
      return [...self, ...children];
    });
  }

  const handleAccordionChange = (event: React.SyntheticEvent, expanded: boolean) => {
    if (isPickerInteractionActive(200)) {
      event.preventDefault?.();
      event.stopPropagation?.();
      return;
    }

    const componentsToStopPropagation = [
      "TimePicker",
      "Html",
      "Button",
      "InputField",
      "MultiSelect",
      "DatePicker",
      "AudioPlayer",
      "Link",
      "Switch",
      "RadioGroup",
      "TimeZonePicker",
      "Avatar",
      "Image",
      "Slider",
      "Checkbox",
    ];
    const verifyIfShouldStopPropagation = collectInteractiveItems(
      local.Header.value?.Items ?? [],
      componentsToStopPropagation
    );

    if (verifyIfShouldStopPropagation?.length > 0) {
      const headerItemIds = verifyIfShouldStopPropagation.map((i) => i.Id);
      const active = document.activeElement as HTMLElement | null;
      const target = event.target as HTMLElement | null;

      const isInsideHeaderItem =
        (active && headerItemIds.some((id) => active.closest(`[data-testid="${id}"]`))) ||
        (target && headerItemIds.some((id) => target.closest(`[data-testid="${id}"]`)));

      if (isInsideHeaderItem) {
        return;
      }
    }

    const [_, classes] = partitionMUIClasses(local.Classes.value);

    const action = publishUserAction(config, context, "accordion_group_change", null, {
      Label: local.Label.value,
      Expanded: local.Expanded.value,
      EventCategory: "engagement",
      ElementClass: classes.join(" "),
    });
    if (!local.ExpandDisabled.value) {
      local.Expanded.value = expanded;
      handlers["onChange"]?.(event, expanded, action);
    }
  };
  return (
    <Accordion
      data-testid={getTestId(config)}
      data-type={config.__typename}
      aria-label={"accordion"}
      className={getClassName(local.Classes)}
      sx={getsxObject(local.Style.value, {
        boxShadow: "none",
        background: "transparent",
        color: "inherit",
        borderRadius: "0px",
      })}
      elevation={config.Elevation ?? 0}
      square={config.Square ?? true}
      expanded={local.Expanded.value}
      onChange={handleAccordionChange}
      slotProps={{
        transition: {
          unmountOnExit: true,
          "data-testid": `${getTestId(config, "collapse-root")}`,
        },
      }}
    >
      <AccordionSummary
        data-testid={`accordion-group-${local.Label.value}`}
        aria-controls={`accordion-group-${local.Label.value}`}
        id={`panel-${config.Id}-header`}
        expandIcon={
          !local.HideExpandIcon.value ? (
            <NGIcon config={{ IconName: "arrow_drop_up" } as Icon} context={context} />
          ) : undefined
        }
        sx={{
          padding: 0,
          ...(local.ExpandDisabled.value ? { cursor: "default !important" } : {}),
        }}
      >
        {!isEmpty(local.Header.value) ? (
          <NGLayoutItem config={local.Header.value} context={context} />
        ) : (
          local.Label.value
        )}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: 0,
          ...getsxObject(local.AccordionContainerStyle.value),
        }}
      >
        {local.Items.value?.map((item, itemKey) => {
          if (isNil(item)) {
            log.error(tag, `Item with id '${item}' could not be found`);
            return <></>;
          }
          return (
            <Box key={`${local.Label.value}-${itemKey}`}>
              {!isLayout(item) ? (
                <NGLayoutItem config={item} context={context} />
              ) : (
                <NGLayout layoutItem={local.LayoutItem.value} layout={item} inDesignMode={false} context={context} />
              )}
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}
